import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className="w-full sm:w-5/6 md:w-3/4 lg:w-4/5 xl:w-4/5 mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl border border-black shadow-lg overflow-hidden">
                <div className="p-6 sm:p-8 md:p-10">
                    {/* Main heading */}
                    <h1 className="text-center text-4xl md:text-5xl font-black text-[#5c3e21] mb-6">
                        Privacy Policy
                    </h1>

                    {/* Subtitle / Company */}
                    <p className="text-start text-gray-500 text-sm mb-8">
                        Acharya For Life Solutions Limited, trading as Smart Acharya
                        <br />
                        Last updated: 28/05/2026
                    </p>

                    {/* 1. Introduction */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">1. Introduction</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        This Privacy Policy explains how Acharya for Life Solutions Limited, trading as Smart Acharya
                        (“we”, “us”, “our”) collects, uses, and protects personal data when providing our AI-powered
                        educational platform to students, schools, and educators in the United Kingdom.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We are committed to safeguarding children’s data and complying with the UK General Data
                        Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                    </p>

                    {/* 2. Who We Are */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">2. Who We Are</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        Acharya for Life Solutions Ltd, trading as Smart Acharya, is a company registered in England and
                        Wales.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        Registered address: 23 Kings Reach, Slough, SL3 7QE
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        Contact email: Info@smartacharya.com

                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Depending on how the Platform is used:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>We act as a <strong>Data Controller</strong> for direct users (e.g., individual subscriptions)</li>
                        <li>We act as a <strong>Data Processor</strong> where we provide services to schools or institutions</li>
                    </ul>

                    {/* 3. Our Services */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">3. Our Services</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We provide an AI-driven learning platform that:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Delivers personalised learning content</li>
                        <li>Uses generative AI to assist with explanations, feedback, and tutoring
                        </li>
                        <li>Tracks student progress and performance
                        </li>
                        <li>Provides analytics to educators and institutions</li>
                    </ul>

                    {/* 4. Data We Collect */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">4. Data We Collect</h2>
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-[#5c3e21] mt-4 mb-2">a. Student Data</h3>
                        <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg">
                            <li>Name, username, and login credentials</li>
                            <li>Age or year group</li>
                            <li>School affiliation (if applicable)</li>
                            <li>Learning activity (answers, assignments, quizzes)</li>
                            <li>AI interactions (prompts, responses, feedback history)</li>
                            <li>Performance data and progress metrics</li>
                        </ul>
                    </div>
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-[#5c3e21] mt-4 mb-2">b. Parent/Guardian Data (if applicable)</h3>
                        <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg">
                            <li>Name and contact details</li>
                            <li>Consent records</li>
                        </ul>
                    </div>
                    <div className="mb-4">
                        <h3 className="text-xl font-bold text-[#5c3e21] mt-4 mb-2">c. Educator / School Data</h3>
                        <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg">
                            <li>Name, email, role, and institution</li>
                            <li>Class and student management data</li>
                            <li>Usage analytics</li>
                        </ul>
                    </div>
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-[#5c3e21] mt-4 mb-2">d. Technical Data</h3>
                        <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg">
                            <li>IP address and device identifiers</li>
                            <li>Browser type and operating system</li>
                            <li>Usage logs and interaction timestamps</li>
                        </ul>
                    </div>

                    {/* 5. How We Use Personal Data */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">5. How We Use Personal Data</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We use personal data to:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Deliver and personalise learning experiences</li>
                        <li>Provide AI-generated tutoring and feedback</li>
                        <li>Track and report academic progress</li>
                        <li>Enable teachers to monitor student performance</li>
                        <li>Improve our AI models and platform functionality</li>
                        <li>Ensure safeguarding and detect misuse</li>
                        <li>Communicate with users and institutions</li>
                        <li>Comply with legal and regulatory obligations</li>
                    </ul>

                    {/* 6. AI Processing and Transparency */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">6. AI Processing and Transparency</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        Our Platform uses machine learning and generative AI systems to:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Analyse student inputs and learning behaviour</li>
                        <li>Generate tailored explanations and recommendations</li>
                        <li>Adapt content difficulty and pacing</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2 font-semibold">Important safeguards:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>AI outputs are monitored and periodically reviewed</li>
                        <li>Significant educational decisions are not made solely by automated means</li>
                        <li>Users can request human review of AI-generated feedback</li>
                        <li>We minimise use of identifiable data in model training where possible</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We do <strong>not</strong> use student data to train general-purpose AI models without explicit consent.
                    </p>

                    {/* 7. Legal Bases for Processing */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">7. Legal Bases for Processing</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We rely on the following lawful bases:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li><strong>Contractual necessity</strong> – delivering the Platform</li>
                        <li><strong>Legitimate interests</strong> – improving services and ensuring security</li>
                        <li><strong>Consent</strong> – for:
                            <ul className="list-[circle] pl-6 mt-1 space-y-1">
                                <li>Marketing communications</li>
                                <li>Certain AI features (where required)</li>
                                <li>Children under 13 (via parent/guardian or school, as appropriate)</li>
                            </ul>
                        </li>
                        <li><strong>Legal obligation</strong> – safeguarding and regulatory compliance</li>
                    </ul>

                    {/* 8. Children’s Data and Safeguarding */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">8. Children’s Data and Safeguarding</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We recognise that many users are under 18.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">We:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Design services with <strong>“privacy by design and by default”</strong> principles</li>
                        <li>Limit data collection to what is necessary</li>
                        <li>Avoid profiling that could negatively impact children</li>
                        <li>Implement content filtering and monitoring</li>
                        <li>Work with schools as data controllers where applicable</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Where required, schools act as the primary consent provider under UK law.
                    </p>

                    {/* 9. Data Sharing */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">9. Data Sharing</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We may share data with:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li> <strong>Schools and educators</strong> (student progress and performance)</li>
                        <li> <strong>Service providers</strong>, including:
                            <ul className="list-[circle] pl-6 mt-1">
                                <li>Cloud hosting providers</li>
                                <li>Analytics tools</li>
                                <li>AI infrastructure providers</li>
                            </ul>
                        </li>
                        <li> <strong>Regulators or law enforcement</strong>, where legally required</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        All third parties are contractually bound to protect personal data and comply with UK GDPR.
                    </p>

                    {/* 10. International Transfers */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">10. International Transfers</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        Some of our service providers may process data outside the UK.<br />
                        Where this occurs, we ensure appropriate safeguards, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>UK adequacy decisions</li>
                        <li>Standard Contractual Clauses (SCCs)</li>
                        <li>Additional technical protections (e.g., encryption, pseudonymisation)</li>
                    </ul>

                    {/* 11. Data Retention */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">11. Data Retention</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We retain data as follows:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Student accounts: for the duration of the educational relationship</li>
                        <li>School-managed accounts: as instructed by the institution</li>
                        <li>Inactive accounts: typically deleted after 12–24 months</li>
                        <li>AI interaction logs: retained for a limited period to improve service quality</li>
                        <li>Legal obligations: retained as required</li>
                    </ul>

                    {/* 12. Your Rights */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">12. Your Rights</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        Individuals have the right to:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Access their personal data</li>
                        <li>Correct inaccurate information</li>
                        <li>Request deletion of data</li>
                        <li>Restrict or object to processing</li>
                        <li>Request data portability</li>
                        <li>Withdraw consent at any time</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        For children, these rights may be exercised by parents/guardians or schools.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Contact: info@smartacharya.com
                    </p>

                    {/* 13. Data Security */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">13. Data Security</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We implement strong security measures, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Encryption in transit and at rest</li>
                        <li>Role-based access controls</li>
                        <li>Regular security testing and monitoring</li>
                        <li>Staff training on data protection</li>
                    </ul>

                    {/* 14. Cookies and Tracking */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">14. Cookies and Tracking</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We use cookies to:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Maintain secure sessions</li>
                        <li>Analyse usage patterns</li>
                        <li>Improve performance</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        You can manage cookies through your browser settings.
                    </p>

                    {/* 15. Changes to This Policy */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">15. Changes to This Policy</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We may update this Privacy Policy periodically. We will notify users or institutions of significant
                        changes where appropriate.
                    </p>

                    {/* 16. Contact and Complaints */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">16. Contact and Complaints</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        If you have questions or concerns:
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        Email: info@smartacharya.com
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed">
                        You have the right to complain to the UK Information Commissioner’s Office (ICO).
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;